import { useEffect, useState, type ReactNode } from 'react';
import { onlyText } from 'react-children-utilities';
import CopyToClipboard from 'react-copy-to-clipboard';
import { CheckIconSmall, CopyIcon } from '../../icon';
import { IconButton } from '../icon-button';
import { Text, TextProps } from '../text';
import { useTooltip } from '../tooltip';
import { CopyToClipboardStyles } from './copyable-text.styles';

export const CopyableText = ({
  children,
  tooltipText = 'Copy',
  tooltipSuccessText = 'Text copied!',
  text,
  trackingId,
  ...rest
}: TextProps & {
  text?: string;
  tooltipText?: string;
  tooltipSuccessText?: string;
  trackingId?: string;
}) => {
  const { triggerProps, tooltipProps, Tooltip } = useTooltip({ hoverDelay: 500 });
  const [copied, setCopied] = useState(false);
  const textStyles = CopyToClipboardStyles.text;
  const tooltipStyles = CopyToClipboardStyles.tooltip({ copied });

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  }, [tooltipProps.isOpen]);

  return (
    <>
      <CopyToClipboard
        text={text || onlyText(children as ReactNode)}
        onCopy={() => setCopied(true)}
        data-trackingid={trackingId}
      >
        <Text {...triggerProps} css={textStyles} {...rest}>
          {children}
        </Text>
      </CopyToClipboard>
      <Tooltip {...tooltipProps} css={tooltipStyles}>
        {copied ? (
          <CheckIconSmall className='copy-status-icon' size={16} />
        ) : (
          <CopyIcon className='copy-status-icon' size={16} />
        )}
        <Text css={{ margin: 0 }} color='white'>
          {copied ? tooltipSuccessText : tooltipText}
        </Text>
      </Tooltip>
    </>
  );
};

export const CopyableTextButton = ({
  children,
  tooltipText = 'Copy',
  tooltipSuccessText = 'Text copied!',
  text,
  trackingId,
  ...rest
}: TextProps & {
  text?: string;
  tooltipText?: string;
  tooltipSuccessText?: string;
  trackingId?: string;
}) => {
  const { triggerProps, tooltipProps, Tooltip } = useTooltip({ hoverDelay: 500 });
  const [copied, setCopied] = useState(false);
  const tooltipStyles = CopyToClipboardStyles.tooltip({ copied });

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  }, [tooltipProps.isOpen]);

  return (
    <>
      <Text {...rest}>{children}</Text>
      <CopyToClipboard
        text={text || onlyText(children as ReactNode)}
        onCopy={() => setCopied(true)}
        data-trackingid={trackingId}
      >
        <IconButton size='small' label='' {...triggerProps}>
          <CopyIcon size={16} />
        </IconButton>
      </CopyToClipboard>
      <Tooltip {...tooltipProps} css={tooltipStyles}>
        {copied ? (
          <CheckIconSmall className='copy-status-icon' size={16} />
        ) : (
          <CopyIcon className='copy-status-icon' size={16} />
        )}
        <Text css={{ margin: 0 }} color='white'>
          {copied ? tooltipSuccessText : tooltipText}
        </Text>
      </Tooltip>
    </>
  );
};
